import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
} from "@mui/material";
import { useSelector } from "react-redux";
import axios from "axios";
import { baseURL } from "services/API";
import { useLandlords, useOrganisations } from "services/hooks/rentals";

const adminBase = `${baseURL}/admin`;

const ReallocateLandlords = () => {
  const token = useSelector((state) => state.token);
  const role = useSelector((state) => state.role);
  const { landlords, isLoadingLandlords } = useLandlords();
  const { organisations, isLoadingOrganisations } = useOrganisations();
  const [organisationId, setOrganisationId] = useState("");
  const [selectedLandlordIds, setSelectedLandlordIds] = useState([]);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [submitting, setSubmitting] = useState(false);

  const loadingOrgs = isLoadingOrganisations;
  const organisationsList = Array.isArray(organisations) ? organisations : [];

  const toggleLandlord = (id) => {
    setSelectedLandlordIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleReallocate = async () => {
    if (!organisationId || selectedLandlordIds.length === 0) {
      setMessage({ type: "error", text: "Select target organisation and at least one landlord" });
      return;
    }
    setSubmitting(true);
    setMessage({ type: "", text: "" });
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const res = await axios.post(
        `${adminBase}/organisations/reallocate_landlords`,
        { organisation_id: Number(organisationId), landlord_ids: selectedLandlordIds },
        { headers }
      );
      setMessage({
        type: "success",
        text: `Reallocated ${res.data?.data?.landlords_updated ?? selectedLandlordIds.length} landlord(s)`,
      });
      setSelectedLandlordIds([]);
    } catch (e) {
      setMessage({
        type: "error",
        text: e?.response?.data?.error || e?.message || "Reallocate failed",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (role !== "admin") {
    return (
      <Box m={2}>
        <Alert severity="warning">Only admins can reallocate landlords.</Alert>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Typography variant="h4" sx={{ mb: 2 }}>
        Reallocate Landlords
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Move selected landlords (and their properties) to a target organisation.
      </Typography>

      {message.text && (
        <Alert
          severity={message.type === "error" ? "error" : "success"}
          sx={{ mb: 2 }}
          onClose={() => setMessage({ type: "", text: "" })}
        >
          {message.text}
        </Alert>
      )}

      <Paper sx={{ p: 3, maxWidth: 600 }}>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Target Organisation</InputLabel>
          <Select
            value={organisationId}
            label="Target Organisation"
            onChange={(e) => setOrganisationId(e.target.value)}
            disabled={loadingOrgs}
          >
            <MenuItem value="">Select organisation</MenuItem>
            {organisationsList.map((org) => (
              <MenuItem key={org.id} value={org.id}>
                {org.name} ({org.code})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Select landlords to move
        </Typography>
        {isLoadingLandlords ? (
          <CircularProgress size={24} />
        ) : (
          <List dense sx={{ maxHeight: 300, overflow: "auto", border: "1px solid #eee", borderRadius: 1 }}>
            {(landlords || []).map((ll) => (
              <ListItem
                key={ll.id}
                onClick={() => toggleLandlord(ll.id)}
                sx={{ cursor: "pointer", bgcolor: selectedLandlordIds.includes(ll.id) ? "action.selected" : undefined }}
              >
                <ListItemText
                  primary={ll.business_name ?? ll.rental_name ?? `Landlord #${ll.id}`}
                  secondary={ll.id}
                />
                {selectedLandlordIds.includes(ll.id) && <Chip size="small" label="Selected" />}
              </ListItem>
            ))}
          </List>
        )}

        <Button
          variant="contained"
          onClick={handleReallocate}
          disabled={submitting || !organisationId || selectedLandlordIds.length === 0}
          sx={{ mt: 2 }}
        >
          {submitting ? <CircularProgress size={24} /> : "Reallocate selected landlords"}
        </Button>
      </Paper>
    </Box>
  );
};

export default ReallocateLandlords;
